import { useCallback } from 'react'
import {
  useEditBasicResultMutation,
  useEditRPnumberItemMutation,
  useEditMedResultMutation,
} from '../mutations/useEditPrescMutation'
import {
  BasicResult,
  Medication,
  Prescription,
} from '../../../../types/models/Presc'

type Params = {
  prescription: Prescription
}

type EditBasicResultParams = {
  key: keyof BasicResult
  value: string
}

type EditRPnumberItemParams = {
  rpIndex: number
  key: 'RP番号' | '用法名称' | '調剤数量'
  value: string
}

type EditMedResultParams = {
  rpIndex: number
  medIndex: number
  // bboxはテキスト編集対象ではない(document_ai/bbox-corrections APIで別途修正)
  key: Exclude<keyof Medication, 'bbox'>
  value: string
}

/**
 * 処方箋編集フック（TanStack Query Mutation 版）
 *
 * 編集成功時は invalidateQueries により処方箋リストが自動更新される。
 * refreshPrescriptions パラメータは不要になった。
 */
export const useEditPresc = ({ prescription }: Params) => {
  const editBasicResultMutation = useEditBasicResultMutation()
  const editRPnumberItemMutation = useEditRPnumberItemMutation()
  const editMedResultMutation = useEditMedResultMutation()

  // 全ての Mutation のいずれかが実行中かどうか
  const updatingPrescription =
    editBasicResultMutation.isPending ||
    editRPnumberItemMutation.isPending ||
    editMedResultMutation.isPending

  const editBasicResult = useCallback(
    async ({ key, value }: EditBasicResultParams) => {
      await editBasicResultMutation.mutateAsync({
        prescription,
        key,
        value,
      })
    },
    [prescription, editBasicResultMutation]
  )

  const editRPnumberItem = useCallback(
    async ({ rpIndex, key, value }: EditRPnumberItemParams) => {
      await editRPnumberItemMutation.mutateAsync({
        prescription,
        rpIndex,
        key,
        value,
      })
    },
    [prescription, editRPnumberItemMutation]
  )

  const editMedResult = useCallback(
    async ({ rpIndex, medIndex, key, value }: EditMedResultParams) => {
      await editMedResultMutation.mutateAsync({
        prescription,
        rpIndex,
        medIndex,
        key,
        value,
      })
    },
    [prescription, editMedResultMutation]
  )

  return {
    updatingPrescription,
    editBasicResult,
    editRPnumberItem,
    editMedResult,
  }
}
